import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import favicon from '../assets/favicon.ico';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));
    
    if (token && user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/member');
      }
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    
    console.log('API URL:', process.env.REACT_APP_API_URL);
    console.log('Login attempt with:', { username: formData.username });

    try {
      setError('');
      setLoading(true);
      console.log('Attempting login with username:', formData.username);

      console.log('Making login request to:', `${process.env.REACT_APP_API_URL}/api/auth/login`);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        formData
      );
      console.log('Login response:', response.data);

      console.log('Login response:', response.data);
      
      if (!response.data.token || !response.data.user) {
        throw new Error('Invalid response from server');
      }

      // Store token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Redirect based on role
      if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/member');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please try again.');
      
      // Clear stored data on error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 0 }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: 'transparent',
          }}
        >
          <Box
            component="img"
            src={favicon}
            alt="Ikshana Logo"
            sx={{
              width: { xs: 64, sm: 80 },
              height: { xs: 64, sm: 80 },
              mb: { xs: 2, sm: 3 },
              objectFit: 'contain'
            }}
          />
          <Typography 
            component="h1" 
            variant="h1"
            align="center" 
            gutterBottom
            sx={{
              fontSize: { xs: '24px', sm: '32px' },
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: '#1d1d1f',
              mb: 3
            }}
          >
            Welcome to Ikshana
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                width: '100%',
                borderRadius: '8px',
                '& .MuiAlert-message': {
                  fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                  fontSize: '15px'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box 
            component="form" 
            onSubmit={handleSubmit} 
            sx={{ 
              mt: 1,
              width: '100%'
            }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleChange}
              disabled={loading}
              error={!!error}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': {
                  fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                  fontSize: '15px',
                  color: '#86868b'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff'
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              error={!!error}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                  fontSize: '15px',
                  color: '#86868b'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff'
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                py: 1.5,
                fontSize: '17px',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#0066cc',
                '&:hover': {
                  backgroundColor: '#0055b3'
                },
                '&:disabled': {
                  backgroundColor: '#0066cc',
                  opacity: 0.5
                }
              }}
              disabled={loading || !formData.username || !formData.password}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
